import {
    CroppedRegion,
    DetectedItem,
    ImageAnalysisCategoryHint,
    ImageAnalysisPrivacyFlags,
    ImageAnalysisRequest,
    ImageAnalysisResponse,
    ImageAnalysisSelectedRegionCandidate,
    ImageSearchStatus,
    ImageSize,
    ImageSearchRequest,
    ImageSearchTarget,
    ImageEmbeddingSearchCandidate,
    ImageEmbeddingSearchResponse,
    Listing,
    LookitItemCategoryId,
    ProductCategoryRoot,
    ProductCluster,
    ProductClusterSearchApiRequest,
    ProductClusterSearchApiResponse,
    ProductClusterSearchApiResultGroup,
    ProductClusterSearchDisplayStatus,
    ProductClusterType,
    ProductSubcategory,
    ProductSearchApiRequest,
    ProductSearchApiResponse,
    ProductSearchIssueCode,
    ProductSearchRecoveryAction,
    ProductSearchUiState,
    SearchTargetAdjustmentStatus,
    SearchTargetRegionSource,
    SelectedRegionCrop,
    SensitiveRegionCandidate,
    SourceStatus
} from './domain';
import {
    lookitImageSearchRequests,
    lookitProductClusters
} from './mock-data';
import { defaultRegionForCategory as defaultDisplayRegionForCategory } from './category-default-regions';
import {
    createRuntimeImageAnalysisService,
    createMockImageAnalysisResponseFromDetectedItems,
    fallbackImageAnalysisResponse,
    ImageAnalysisService,
    mockImageAnalysisService
} from './image-analysis-service';
import {
    mockSensitiveRegionService,
    SensitiveRegionDetectionRequest,
    SensitiveRegionDetectionResponse,
    SensitiveRegionService
} from './sensitive-region-service';
import { mockPlatformAdapterRegistry } from './mock-platform-adapter';
import {
    mockVectorSearchRepository,
    vectorSearchResponseToImageEmbeddingSearchResponse,
    VectorSearchCategoryFilter,
    VectorSearchRepository
} from './vector-search-repository';
import {
    productCatalogRepository,
    ProductCatalogRepository
} from './product-service';
import {
    PlatformRawListingBase,
    PlatformSearchProductsRequest,
    PlatformSearchProductsResult
} from './platform-adapter';
import {
    PermissionGuardedPlatformAdapterRegistry,
    PlatformAdapterActivation
} from './platform-permission';
import {
    createNaverShoppingPlatformAdapterRegistry,
    NaverShoppingPlatformAdapterRegistrationOptions,
    NaverShoppingRawListing
} from './naver-shopping-platform-adapter';
import {
    buildNaverShoppingQuery,
    NaverShoppingQueryBuilderInput,
    NaverShoppingQueryBuilderResult
} from './query-builder';

export interface DetectedSearchItem extends DetectedItem {
    id: string;
    label: string;
}

export type { LookitItemCategoryId } from './domain';

export type ImageSearchRequestSession = Omit<ImageSearchRequest, 'detected_items'> & {
    detected_items: DetectedSearchItem[];
    image_analysis?: ImageAnalysisResponse | null;
};

export interface CreateImageSearchRequestInput {
    userId?: string | null;
    uploadImageId?: string | null;
    localMockImageId?: string | null;
    imageUrl: string;
    imageSize?: ImageSize | null;
    fileName?: string;
    fileSize?: number;
    croppedRegion?: CroppedRegion | null;
    detectedItems?: DetectedSearchItem[];
    selectedRegionCandidates?: ImageAnalysisSelectedRegionCandidate[];
    categoryHints?: ImageAnalysisCategoryHint[];
    privacyFlags?: ImageAnalysisPrivacyFlags;
    privacyRedactions?: SensitiveRegionCandidate[];
    cropImageId?: string | null;
    cropRegionOriginal?: CroppedRegion | null;
    selectedCrop?: SelectedRegionCrop | null;
    originalImageSize?: ImageSize | null;
    displayedImageSize?: ImageSize | null;
    regionSource?: SearchTargetRegionSource | null;
    userConfirmed?: boolean;
    detectedCategoryId?: LookitItemCategoryId | string | null;
    suggestedCategoryId?: LookitItemCategoryId | string | null;
    selectedCategoryId?: LookitItemCategoryId | string | null;
    searchTargets?: ImageSearchTarget[];
    activeSearchTargetId?: string | null;
    retentionDays?: number;
}

export interface SelectDetectedItemInput {
    request: ImageSearchRequestSession;
    itemIndex: number;
}

export interface AnalyzeConfirmedCropInput {
    request: ImageSearchRequestSession;
    selectedRegion: CroppedRegion;
    cropImageId?: string | null;
    cropRegionOriginal?: CroppedRegion | null;
    selectedCrop?: SelectedRegionCrop | null;
    originalImageSize?: ImageSize | null;
    displayedImageSize?: ImageSize | null;
    selectedCategoryId: LookitItemCategoryId | string;
    regionSource: SearchTargetRegionSource;
    selectedCandidateId?: string | null;
    userConfirmed?: boolean;
    privacyFlags?: ImageAnalysisPrivacyFlags;
}

export interface SelectedRegionSearchRequest extends ProductSearchApiRequest {
    local_mock_image_id: string | null;
    limit?: number;
}

export type GetSearchResultsInput = SelectedRegionSearchRequest;

export interface SearchResultGroup {
    cluster_type: ProductClusterType;
    label: string;
    source_status: SourceStatus;
    embedding_candidates: ImageEmbeddingSearchCandidate[];
    product_clusters: ProductCluster[];
}

export interface SearchResultsResponse extends Omit<ProductSearchApiResponse, 'groups'> {
    request_id: string;
    upload_image_id: string | null;
    local_mock_image_id: string | null;
    selected_item_index: number | null;
    selected_category_id: LookitItemCategoryId | string | null;
    suggested_category_id?: LookitItemCategoryId | string | null;
    detected_category_id?: LookitItemCategoryId | string | null;
    selected_candidate_id: string | null;
    selected_item_label?: string | null;
    selected_search_target: ImageSearchTarget;
    selected_region: CroppedRegion;
    cropped_region: CroppedRegion | null;
    source_status: SourceStatus;
    image_embedding_search: ImageEmbeddingSearchResponse | null;
    groups: SearchResultGroup[];
    product_clusters: ProductCluster[];
}

export interface ProductSearchPlatformRegistry<
    TRawListing extends PlatformRawListingBase = PlatformRawListingBase
> {
    searchProducts(request: PlatformSearchProductsRequest): Promise<PlatformSearchProductsResult<TRawListing>>;
    getActivations?(): PlatformAdapterActivation<TRawListing>[];
}

export class GuardedPlatformSearchRegistry<
    TRawListing extends PlatformRawListingBase = PlatformRawListingBase
> implements ProductSearchPlatformRegistry<TRawListing> {
    constructor(private readonly registry: PermissionGuardedPlatformAdapterRegistry<TRawListing>) {}

    getActivations(): PlatformAdapterActivation<TRawListing>[] {
        return this.registry.getActivations();
    }

    async searchProducts(request: PlatformSearchProductsRequest = {}): Promise<PlatformSearchProductsResult<TRawListing>> {
        const rawListings: TRawListing[] = [];
        const listings: Listing[] = [];

        for (const adapter of this.registry.getActiveAdapters()) {
            const adapterRawListings = await adapter.searchProducts(request);
            adapterRawListings.forEach((rawListing) => {
                rawListings.push(rawListing);
                listings.push(adapter.normalizeListing(rawListing));
            });
        }

        return {
            requested_at: new Date().toISOString(),
            raw_listings: rawListings,
            listings
        };
    }
}

export function createGuardedPlatformSearchRegistry<
    TRawListing extends PlatformRawListingBase = PlatformRawListingBase
>(
    registry: PermissionGuardedPlatformAdapterRegistry<TRawListing>
): ProductSearchPlatformRegistry<TRawListing> {
    return new GuardedPlatformSearchRegistry(registry);
}

export function createNaverShoppingSearchRegistry(
    options: NaverShoppingPlatformAdapterRegistrationOptions = {}
): ProductSearchPlatformRegistry<NaverShoppingRawListing> {
    return createGuardedPlatformSearchRegistry(createNaverShoppingPlatformAdapterRegistry(options));
}

function createDefaultExternalPlatformSearchRegistries(): ProductSearchPlatformRegistry[] {
    return [
        createNaverShoppingSearchRegistry()
    ];
}

let activeImageAnalysisService: ImageAnalysisService = createRuntimeImageAnalysisService();
let activeSensitiveRegionService: SensitiveRegionService = mockSensitiveRegionService;
let activeVectorSearchRepository: VectorSearchRepository = mockVectorSearchRepository;
let activeProductCatalogRepository: ProductCatalogRepository = productCatalogRepository;
let activeExternalPlatformSearchRegistries: ProductSearchPlatformRegistry[] = createDefaultExternalPlatformSearchRegistries();

export function setImageAnalysisService(service: ImageAnalysisService) {
    activeImageAnalysisService = service;
}

export function resetImageAnalysisService() {
    activeImageAnalysisService = mockImageAnalysisService;
}

export function setSensitiveRegionService(service: SensitiveRegionService) {
    activeSensitiveRegionService = service;
}

export function resetSensitiveRegionService() {
    activeSensitiveRegionService = mockSensitiveRegionService;
}

export function setVectorSearchRepository(repository: VectorSearchRepository) {
    activeVectorSearchRepository = repository;
}

export function resetVectorSearchRepository() {
    activeVectorSearchRepository = mockVectorSearchRepository;
}

export function setProductCatalogRepository(repository: ProductCatalogRepository) {
    activeProductCatalogRepository = repository;
}

export function resetProductCatalogRepository() {
    activeProductCatalogRepository = productCatalogRepository;
}

export function setProductSearchPlatformRegistries(registries: ProductSearchPlatformRegistry[]) {
    activeExternalPlatformSearchRegistries = registries.slice();
}

export function resetProductSearchPlatformRegistries() {
    activeExternalPlatformSearchRegistries = createDefaultExternalPlatformSearchRegistries();
}

export function useRuntimeImageAnalysisService() {
    activeImageAnalysisService = createRuntimeImageAnalysisService();
    return activeImageAnalysisService;
}

export interface InitialSearchRecommendation {
    selectedRegion: CroppedRegion;
    regionSource: SearchTargetRegionSource;
    adjustmentStatus: SearchTargetAdjustmentStatus;
    selectedCandidateId: string | null;
    selectedItemIndex: number | null;
    suggestedCategoryId: LookitItemCategoryId | string | null;
    categoryConfidence: number | null;
    selectedCategoryId: LookitItemCategoryId | string | null;
    searchTargetId: string;
    source: 'detected_candidate' | 'search_target';
    confidence: number | null;
    needsCategoryConfirmation: boolean;
}

export const initialSearchRecommendationPolicy = {
    autoCategoryMinConfidence: 0.8
} as const;

const clusterOrder: ProductClusterType[] = ['brand_exact', 'wholesale_same', 'similar_style'];
const defaultSearchTargetCategoryIds: LookitItemCategoryId[] = ['top', 'bottom', 'shoes'];
const categoryIntentClusterIds: Record<string, string[]> = {
    top: ['cluster-liette-lace-blouse', 'cluster-similar-pintuck-blouse'],
    'top:블라우스': ['cluster-liette-lace-blouse', 'cluster-similar-pintuck-blouse'],
    'top:니트': ['cluster-forest-zipup'],
    bottom: ['cluster-muse-straight-denim', 'cluster-vegetable-cargo-pants', 'cluster-similar-wide-slacks'],
    'bottom:팬츠': ['cluster-muse-straight-denim', 'cluster-vegetable-cargo-pants', 'cluster-similar-wide-slacks'],
    'bottom:데님': ['cluster-muse-straight-denim', 'cluster-vegetable-cargo-pants', 'cluster-similar-wide-slacks'],
    'bottom:스커트': [],
    'bottom:쇼츠': [],
    'bottom:레깅스': []
};

interface CategoryParts {
    rootLabel: ProductCategoryRoot | null;
    subcategoryLabel: ProductSubcategory | null;
}

interface SearchCategoryContext extends CategoryParts {
    categoryId: string | null;
    matchingSubcategoryLabel: ProductSubcategory | null;
    isSubcategoryConfirmed: boolean;
    intentKey: string | null;
}

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function addDays(date: Date, days: number): Date {
    const next = new Date(date.getTime());
    next.setDate(next.getDate() + days);
    return next;
}

function groupLabel(clusterType: ProductClusterType, context?: SearchCategoryContext): string {
    const basisLabel = context?.subcategoryLabel || context?.rootLabel || '';
    const prefix = basisLabel ? `${basisLabel} 기준 · ` : '';

    if (clusterType === 'brand_exact') return `${prefix}동일 상품 가능성 높음`;
    if (clusterType === 'wholesale_same') return `${prefix}같은 도매택/동일 공급 상품 추정`;
    return `${prefix}유사 디자인 상품`;
}

function groupSourceStatus(productClusters: ProductCluster[]): SourceStatus {
    if (productClusters.length === 0) return 'api_pending';
    if (productClusters.some((cluster) => cluster.source_status === 'needs_verification')) return 'needs_verification';
    if (productClusters.some((cluster) => cluster.source_status === 'mock')) return 'mock';
    if (productClusters.some((cluster) => cluster.source_status === 'api_pending')) return 'api_pending';
    return 'image_match_ready';
}

function normalizeSubcategory(value?: string | null): ProductSubcategory | null {
    const normalizedValue = (value || '').trim().toLowerCase();
    if (!normalizedValue) return null;

    const aliases: Record<string, ProductSubcategory> = {
        pants: '팬츠',
        pant: '팬츠',
        trouser: '팬츠',
        trousers: '팬츠',
        slacks: '팬츠',
        '슬랙스': '팬츠',
        '바지': '팬츠',
        '팬츠': '팬츠',
        denim: '데님',
        jeans: '데님',
        jean: '데님',
        '청바지': '데님',
        '데님': '데님',
        skirt: '스커트',
        '스커트': '스커트',
        shorts: '쇼츠',
        short: '쇼츠',
        '쇼츠': '쇼츠',
        '반바지': '쇼츠',
        leggings: '레깅스',
        legging: '레깅스',
        '레깅스': '레깅스',
        blouse: '블라우스',
        '블라우스': '블라우스',
        tshirt: '티셔츠',
        't-shirt': '티셔츠',
        tee: '티셔츠',
        '티셔츠': '티셔츠',
        knit: '니트',
        '니트': '니트',
        shirt: '셔츠',
        '셔츠': '셔츠',
        cardigan: '가디건',
        '가디건': '가디건'
    };

    return aliases[normalizedValue] || value as ProductSubcategory;
}

function categoryPartsFromText(category?: string | null): CategoryParts {
    const [root, subcategory] = (category || '').split('>').map((part) => part.trim()).filter(Boolean);

    return {
        rootLabel: root ? root as ProductCategoryRoot : null,
        subcategoryLabel: normalizeSubcategory(subcategory)
    };
}

function categoryBaseId(categoryId?: string | null): string | null {
    const [baseId] = (categoryId || '').split(':');
    return baseId || null;
}

function subcategoryFromCategoryId(categoryId?: string | null): ProductSubcategory | null {
    const parts = (categoryId || '').split(':');
    return normalizeSubcategory(parts.length > 1 ? parts.slice(1).join(':') : null);
}

function subcategoryForDetectedItemLabel(label?: string | null): ProductSubcategory | null {
    return categoryPartsFromText(label || null).subcategoryLabel;
}

function defaultMatchingSubcategory(categoryId?: string | null): ProductSubcategory | null {
    if (categoryBaseId(categoryId) === 'bottom') return '팬츠';
    return null;
}

function compatibleExactSubcategories(subcategory?: ProductSubcategory | null): Set<ProductSubcategory> | null {
    if (!subcategory) return null;
    if (subcategory === '팬츠' || subcategory === '데님') return new Set<ProductSubcategory>(['팬츠', '데님']);
    return new Set<ProductSubcategory>([subcategory]);
}

function isPantsDenimSubcategory(subcategory?: ProductSubcategory | null): boolean {
    return subcategory === '팬츠' || subcategory === '데님';
}

function clusterSubcategory(cluster: ProductCluster): ProductSubcategory | null {
    return normalizeSubcategory(cluster.subcategory) || categoryPartsFromText(cluster.category).subcategoryLabel;
}

function clusterNameHasSkirtSignal(cluster: ProductCluster): boolean {
    return /스커트|skirt/i.test(cluster.canonical_name);
}

function clusterNameHasPantsSignal(cluster: ProductCluster): boolean {
    return /팬츠|바지|데님|청바지|슬랙스|쇼츠|반바지|레깅스|pants?|trousers?|denim|jeans?|slacks|shorts?|leggings?/i.test(cluster.canonical_name);
}

function clusterMatchesBottomSubcategory(cluster: ProductCluster, context: SearchCategoryContext): boolean {
    if (context.rootLabel !== '하의') return true;

    const targetSubcategory = normalizeSubcategory(context.matchingSubcategoryLabel);
    if (!targetSubcategory) return true;

    const allowedSubcategories = compatibleExactSubcategories(targetSubcategory);
    const productSubcategory = clusterSubcategory(cluster);

    if (productSubcategory && allowedSubcategories) {
        return allowedSubcategories.has(productSubcategory);
    }

    if (isPantsDenimSubcategory(targetSubcategory) && clusterNameHasSkirtSignal(cluster)) return false;
    if (targetSubcategory === '스커트' && clusterNameHasPantsSignal(cluster)) return false;

    return true;
}

function searchContextForInput(input: { selected_category_id?: string | null; selected_item_label?: string | null }): SearchCategoryContext {
    const categoryId = input.selected_category_id || null;
    const rootLabel = categoryRootForId(categoryId);
    const explicitSubcategory = subcategoryFromCategoryId(categoryId);
    const candidateSubcategory = subcategoryForDetectedItemLabel(input.selected_item_label);
    const matchingSubcategoryLabel = explicitSubcategory || candidateSubcategory || defaultMatchingSubcategory(categoryId);
    const isSubcategoryConfirmed = Boolean(explicitSubcategory || candidateSubcategory);
    const subcategoryLabel = isSubcategoryConfirmed ? matchingSubcategoryLabel : null;
    const intentKey = categoryBaseId(categoryId)
        ? [categoryBaseId(categoryId), matchingSubcategoryLabel].filter(Boolean).join(':')
        : null;

    return {
        categoryId,
        rootLabel,
        subcategoryLabel,
        matchingSubcategoryLabel,
        isSubcategoryConfirmed,
        intentKey
    };
}

function categoryRootForId(categoryId?: string | null): ProductCategoryRoot | null {
    const baseId = categoryBaseId(categoryId);
    if (baseId === 'top') return '상의';
    if (baseId === 'bottom') return '하의';
    if (baseId === 'outer') return '아우터';
    if (baseId === 'dress') return '원피스';
    if (baseId === 'shoes') return '신발';
    if (baseId === 'bag') return '가방';
    if (baseId === 'accessory') return '액세서리';
    if (baseId === 'unknown') return null;
    if (baseId === 'other') return null;
    return null;
}

function categoryLabelForId(categoryId?: string | null): string {
    const baseId = categoryBaseId(categoryId);
    if (baseId === 'top') return '상의';
    if (baseId === 'bottom') return '하의';
    if (baseId === 'outer') return '아우터';
    if (baseId === 'dress') return '원피스';
    if (baseId === 'shoes') return '신발';
    if (baseId === 'bag') return '가방';
    if (baseId === 'accessory') return '액세서리';
    if (baseId === 'unknown') return '확인 필요';
    if (baseId === 'other') return '기타';
    return '검색';
}

function usableRegion(region?: CroppedRegion | null): CroppedRegion | null {
    if (!region) return null;
    if (region.width <= 0 || region.height <= 0) return null;
    return region;
}

function detectedItemRegion(item: DetectedSearchItem): CroppedRegion | null {
    return usableRegion(item.cropped_region) || usableRegion(item.boundingBox);
}

function categoryConfidenceForDetectedItem(item: DetectedSearchItem): number | null {
    const confidence = item.suggestedCategoryConfidence ?? item.categoryConfidence ?? null;
    return typeof confidence === 'number' && Number.isFinite(confidence) ? confidence : null;
}

function detectedCandidatePriority(item: DetectedSearchItem): number {
    const confidence = typeof item.confidence === 'number' && Number.isFinite(item.confidence) ? item.confidence : 0;
    const confirmationPenalty = item.needsUserConfirmation ? 0.15 : 0;
    return confidence - confirmationPenalty;
}

function compareDetectedCandidates(a: DetectedSearchItem, b: DetectedSearchItem): number {
    const priorityDiff = detectedCandidatePriority(b) - detectedCandidatePriority(a);
    if (priorityDiff !== 0) return priorityDiff;
    return a.item_index - b.item_index;
}

function bestDetectedCandidate(items: DetectedSearchItem[]): DetectedSearchItem | null {
    return [...items]
        .filter((item) => Boolean(detectedItemRegion(item)))
        .sort(compareDetectedCandidates)[0] || null;
}

function canAutoSelectDetectedCategory(item: DetectedSearchItem): boolean {
    const suggestedCategoryId = item.suggestedCategoryId || null;
    const categoryConfidence = categoryConfidenceForDetectedItem(item);
    return Boolean(
        suggestedCategoryId &&
        !item.needsUserConfirmation &&
        categoryConfidence !== null &&
        categoryConfidence >= initialSearchRecommendationPolicy.autoCategoryMinConfidence
    );
}

function defaultRegionForCategory(categoryId?: string | null): CroppedRegion | null {
    return defaultDisplayRegionForCategory(categoryId);
}

function targetIdForCategory(categoryId: string): string {
    return `target-${categoryId}`;
}

function targetIdForDetectedCandidate(candidateId: string): string {
    return `target-candidate-${candidateId}`;
}

function adjustmentStatusForRegionSource(regionSource: SearchTargetRegionSource): SearchTargetAdjustmentStatus {
    if (regionSource === 'category_default') return 'defaulted';
    if (regionSource === 'candidate') return 'candidate';
    if (regionSource === 'manual') return 'manual';
    if (regionSource === 'adjusted') return 'adjusted';
    if (regionSource === 'full_image') return 'full_image';
    return 'unselected';
}

function createSearchTarget(categoryId: string, now: string): ImageSearchTarget {
    const selectedRegion = defaultRegionForCategory(categoryId);
    const regionSource: SearchTargetRegionSource = selectedRegion ? 'category_default' : 'none';

    return {
        id: targetIdForCategory(categoryId),
        label: `${categoryLabelForId(categoryId)} 타깃`,
        selectedCategory: categoryId,
        selectedRegion,
        regionSource,
        adjustmentStatus: adjustmentStatusForRegionSource(regionSource),
        suggestedCategoryId: null,
        suggestedCategoryConfidence: null,
        selectedCandidateId: null,
        selectedItemIndex: null,
        updatedAt: now
    };
}

function createInitialSearchTargets(activeCategoryId: string | null, now: string): ImageSearchTarget[] {
    const categoryIds = new Set<string>(defaultSearchTargetCategoryIds);
    if (activeCategoryId) categoryIds.add(activeCategoryId);
    return Array.from(categoryIds).map((categoryId) => createSearchTarget(categoryId, now));
}

function resolveSearchTarget(input: SelectedRegionSearchRequest): ImageSearchTarget {
    const categoryId = input.selected_category_id;
    const regionSource = input.region_source || 'manual';

    return {
        id: input.search_target_id || targetIdForCategory(categoryId),
        label: `${categoryLabelForId(categoryId)} 타깃`,
        selectedCategory: categoryId,
        selectedRegion: input.selected_region,
        regionSource,
        adjustmentStatus: input.adjustment_status || adjustmentStatusForRegionSource(regionSource),
        suggestedCategoryId: input.suggested_category_id || null,
        suggestedCategoryConfidence: null,
        selectedCandidateId: input.selected_candidate_id,
        selectedItemIndex: input.selected_item_index ?? null,
        updatedAt: new Date().toISOString()
    };
}

function clusterMatchesCategory(cluster: ProductCluster, context: SearchCategoryContext): boolean {
    if (!context.rootLabel) return true;
    if (cluster.category_root !== context.rootLabel) return false;
    return clusterMatchesBottomSubcategory(cluster, context);
}

function effectiveClusterType(cluster: ProductCluster, context: SearchCategoryContext): ProductClusterType {
    const exactSubcategories = compatibleExactSubcategories(context.matchingSubcategoryLabel);
    if (!exactSubcategories || cluster.cluster_type === 'similar_style') return cluster.cluster_type;
    if (!cluster.subcategory || exactSubcategories.has(cluster.subcategory)) return cluster.cluster_type;
    return 'similar_style';
}

function clusterWithEffectiveType(cluster: ProductCluster, context: SearchCategoryContext): ProductCluster {
    const clusterType = effectiveClusterType(cluster, context);
    if (clusterType === cluster.cluster_type) return cluster;

    return {
        ...cluster,
        cluster_type: clusterType,
        confidence_level: Math.min(cluster.confidence_level, 0.64)
    };
}

function orderedProductClusters(limit?: number, context: SearchCategoryContext = searchContextForInput({ selected_category_id: null, selected_item_label: null })): ProductCluster[] {
    const preferredIds = context.intentKey
        ? categoryIntentClusterIds[context.intentKey] || categoryIntentClusterIds[categoryBaseId(context.categoryId) || ''] || []
        : [];
    const preferredIdSet = new Set(preferredIds);
    const sourceClusters = preferredIds.length > 0
        ? lookitProductClusters.filter((cluster) => preferredIdSet.has(cluster.id))
        : lookitProductClusters.filter((cluster) => clusterMatchesCategory(cluster, context));
    const clusters = sourceClusters
        .filter((cluster) => clusterMatchesCategory(cluster, context))
        .map((cluster) => clusterWithEffectiveType(cluster, context))
        .sort((a, b) => {
        const priorityDiff = (preferredIds.indexOf(a.id) === -1 ? Number.MAX_SAFE_INTEGER : preferredIds.indexOf(a.id)) -
            (preferredIds.indexOf(b.id) === -1 ? Number.MAX_SAFE_INTEGER : preferredIds.indexOf(b.id));
        if (priorityDiff !== 0) return priorityDiff;

        const typeDiff = clusterOrder.indexOf(a.cluster_type) - clusterOrder.indexOf(b.cluster_type);
        if (typeDiff !== 0) return typeDiff;
        return b.confidence_level - a.confidence_level;
    });

    return cloneData(typeof limit === 'number' ? clusters.slice(0, limit) : clusters);
}

function isRecommendedSearchTarget(target: ImageSearchTarget): boolean {
    return Boolean(
        usableRegion(target.selectedRegion) &&
        (
            target.regionSource === 'candidate' ||
            target.adjustmentStatus === 'candidate' ||
            Boolean(target.selectedCandidateId)
        )
    );
}

function targetCategoryConfidence(target: ImageSearchTarget): number | null {
    const confidence = target.suggestedCategoryConfidence ?? null;
    return typeof confidence === 'number' && Number.isFinite(confidence) ? confidence : null;
}

function canAutoSelectTargetCategory(target: ImageSearchTarget): boolean {
    const confidence = targetCategoryConfidence(target);
    return Boolean(
        target.suggestedCategoryId &&
        confidence !== null &&
        confidence >= initialSearchRecommendationPolicy.autoCategoryMinConfidence
    );
}

function regionsMatch(a?: CroppedRegion | null, b?: CroppedRegion | null): boolean {
    if (!a || !b) return false;
    return Math.abs(a.x - b.x) < 0.01 &&
        Math.abs(a.y - b.y) < 0.01 &&
        Math.abs(a.width - b.width) < 0.01 &&
        Math.abs(a.height - b.height) < 0.01;
}

function detectedCandidateForAnalysisResponse(response: ImageAnalysisResponse, items: DetectedSearchItem[]): DetectedSearchItem | null {
    const suggestedRegion = response.suggestedRegion;
    if (suggestedRegion) {
        const matchingCandidate = items.find((item) => regionsMatch(detectedItemRegion(item), suggestedRegion)) || null;
        if (matchingCandidate) return matchingCandidate;
    }

    return bestDetectedCandidate(items);
}

function canAutoSelectAnalysisCategory(response: ImageAnalysisResponse, categoryConfidence: number | null): boolean {
    const confidence = response.confidence ?? categoryConfidence;
    return Boolean(
        response.suggestedCategoryId &&
        !response.needsUserConfirmation &&
        confidence !== null &&
        confidence >= initialSearchRecommendationPolicy.autoCategoryMinConfidence
    );
}

export function getInitialSearchRecommendation(request: ImageSearchRequestSession): InitialSearchRecommendation | null {
    const analysis = request.image_analysis || null;
    if (analysis?.suggestedRegion) {
        const candidate = detectedCandidateForAnalysisResponse(analysis, request.detected_items || []);
        const suggestedCategoryId = analysis.suggestedCategoryId || candidate?.suggestedCategoryId || null;
        const categoryConfidence = candidate ? categoryConfidenceForDetectedItem(candidate) : analysis.confidence;
        const selectedCategoryId = canAutoSelectAnalysisCategory(analysis, categoryConfidence) ? suggestedCategoryId : null;
        const searchTargetId = selectedCategoryId
            ? targetIdForCategory(selectedCategoryId)
            : candidate
                ? targetIdForDetectedCandidate(candidate.id)
                : `target-analysis-${request.id}`;

        return {
            selectedRegion: cloneData(analysis.suggestedRegion),
            regionSource: 'candidate',
            adjustmentStatus: 'candidate',
            selectedCandidateId: candidate?.id || null,
            selectedItemIndex: candidate?.item_index ?? null,
            suggestedCategoryId,
            categoryConfidence,
            selectedCategoryId,
            searchTargetId,
            source: candidate ? 'detected_candidate' : 'search_target',
            confidence: analysis.confidence,
            needsCategoryConfirmation: !selectedCategoryId
        };
    }

    if (analysis && analysis.status !== 'detected') return null;

    const candidate = bestDetectedCandidate(request.detected_items || []);

    if (candidate) {
        const selectedRegion = detectedItemRegion(candidate);
        if (!selectedRegion) return null;

        const suggestedCategoryId = candidate.suggestedCategoryId || null;
        const categoryConfidence = categoryConfidenceForDetectedItem(candidate);
        const selectedCategoryId = canAutoSelectDetectedCategory(candidate) ? suggestedCategoryId : null;

        return {
            selectedRegion: cloneData(selectedRegion),
            regionSource: 'candidate',
            adjustmentStatus: 'candidate',
            selectedCandidateId: candidate.id,
            selectedItemIndex: candidate.item_index,
            suggestedCategoryId,
            categoryConfidence,
            selectedCategoryId,
            searchTargetId: selectedCategoryId ? targetIdForCategory(selectedCategoryId) : targetIdForDetectedCandidate(candidate.id),
            source: 'detected_candidate',
            confidence: candidate.confidence,
            needsCategoryConfirmation: !selectedCategoryId
        };
    }

    const target = (request.search_targets || []).find(isRecommendedSearchTarget) || null;
    if (!target || !target.selectedRegion) return null;

    const selectedCategoryId = canAutoSelectTargetCategory(target) ? target.suggestedCategoryId || null : null;
    const confidence = targetCategoryConfidence(target);

    return {
        selectedRegion: cloneData(target.selectedRegion),
        regionSource: target.regionSource || 'candidate',
        adjustmentStatus: target.adjustmentStatus || adjustmentStatusForRegionSource(target.regionSource || 'candidate'),
        selectedCandidateId: target.selectedCandidateId || null,
        selectedItemIndex: target.selectedItemIndex ?? null,
        suggestedCategoryId: target.suggestedCategoryId || null,
        categoryConfidence: confidence,
        selectedCategoryId,
        searchTargetId: target.id,
        source: 'search_target',
        confidence,
        needsCategoryConfirmation: !selectedCategoryId
    };
}

function imageAnalysisUiState(response: ImageAnalysisResponse): ProductSearchUiState {
    if (response.status === 'detected') {
        return {
            status: response.needsUserConfirmation ? 'needs_user_confirmation' : 'ready',
            issues: []
        };
    }

    return {
        status: response.status,
        issues: [
            {
                code: response.issueCode || response.status,
                message: response.message || '자동 감지 결과를 확인해 주세요.',
                retryable: true
            }
        ]
    };
}

function searchIssue(code: ProductSearchIssueCode, message: string, recommendedActions: ProductSearchRecoveryAction[]): ProductSearchUiState['issues'][number] {
    return {
        code,
        message,
        retryable: true,
        recommended_actions: recommendedActions
    };
}

function emptyGroupsForSearchContext(searchContext: SearchCategoryContext): SearchResultGroup[] {
    return clusterOrder.map((clusterType) => ({
        cluster_type: clusterType,
        label: groupLabel(clusterType, searchContext),
        source_status: 'api_pending',
        embedding_candidates: [],
        product_clusters: []
    }));
}

function resultUiState(input: {
    productClusters: ProductCluster[];
    groups: SearchResultGroup[];
    candidateProductClusters: ProductCluster[];
    platformErrorMessage?: string | null;
}): ProductSearchUiState {
    if (input.platformErrorMessage) {
        return {
            status: 'platform_error',
            issues: [
                searchIssue(
                    'platform_error',
                    input.platformErrorMessage,
                    ['adjust_region', 'change_category', 'search_full_image', 'upload_new_photo']
                )
            ]
        };
    }

    if (input.productClusters.length === 0) {
        const message = input.candidateProductClusters.length > 0
            ? '상품 후보는 있었지만 현재 연결 가능한 판매처가 없습니다.'
            : '선택한 영역과 카테고리에서 판매 가능한 상품 후보가 없습니다.';

        return {
            status: 'no_listing_available',
            issues: [
                searchIssue(
                    'no_listing_available',
                    message,
                    ['adjust_region', 'change_category', 'search_full_image', 'upload_new_photo']
                )
            ]
        };
    }

    const exactCount = input.groups.find((group) => group.cluster_type === 'brand_exact')?.product_clusters.length || 0;
    const wholesaleCount = input.groups.find((group) => group.cluster_type === 'wholesale_same')?.product_clusters.length || 0;
    const similarCount = input.groups.find((group) => group.cluster_type === 'similar_style')?.product_clusters.length || 0;

    if (exactCount === 0 && wholesaleCount === 0 && similarCount > 0) {
        return {
            status: 'no_exact_match',
            issues: [
                searchIssue(
                    'no_exact_match',
                    '동일 상품이나 같은 도매택으로 볼 근거가 부족해 유사 디자인만 분리했습니다.',
                    ['view_similar_designs', 'adjust_region', 'change_category', 'upload_new_photo']
                )
            ]
        };
    }

    return {
        status: 'completed',
        issues: []
    };
}

function searchStatusForImageAnalysis(response: ImageAnalysisResponse): ImageSearchStatus {
    if (response.status === 'detected' && !response.needsUserConfirmation) return 'detecting';
    return 'awaiting_user_confirmation';
}

function imageAnalysisRequestFromInput(input: CreateImageSearchRequestInput, localMockImageId: string | null): ImageAnalysisRequest {
    const selectedCrop = input.selectedCrop || null;
    const cropImageId = input.cropImageId || selectedCrop?.crop_image_id || null;
    const cropRegionOriginal = input.cropRegionOriginal || selectedCrop?.crop_region_original || null;
    const selectedRegion = input.croppedRegion || selectedCrop?.selected_region || null;
    const selectedCategoryId = input.selectedCategoryId || input.suggestedCategoryId || null;

    return {
        uploadImageId: input.uploadImageId ?? null,
        cropImageId,
        cropUrl: selectedCrop?.crop_url || null,
        localMockImageId,
        imageUrl: input.imageUrl,
        imageSize: input.imageSize || input.originalImageSize || null,
        selectedRegion,
        cropRegionOriginal,
        selectedCategoryId,
        regionSource: input.regionSource || (selectedRegion ? 'manual' : 'none'),
        userConfirmed: input.userConfirmed ?? Boolean(selectedRegion && selectedCategoryId && cropRegionOriginal),
        selectedRegionCandidates: input.selectedRegionCandidates || [],
        categoryHints: input.categoryHints || [],
        categoryHint: input.categoryHints?.[0] || input.selectedCategoryId || input.suggestedCategoryId || null,
        privacyFlags: input.privacyFlags || {
            allowExternalAnalysis: false,
            requiresOnDeviceProcessing: true,
            retainUploadedImage: false
        },
        fileName: input.fileName,
        fileSize: input.fileSize
    };
}

function imageAnalysisRequestFromConfirmedCrop(input: AnalyzeConfirmedCropInput): ImageAnalysisRequest {
    const selectedCrop = input.selectedCrop || null;
    const cropImageId = input.cropImageId || selectedCrop?.crop_image_id || null;
    const cropRegionOriginal = input.cropRegionOriginal || selectedCrop?.crop_region_original || null;
    const selectedRegionCandidates: ImageAnalysisSelectedRegionCandidate[] = [
        {
            id: cropImageId || input.selectedCandidateId || 'confirmed-crop',
            region: cloneData(input.selectedRegion),
            source: input.regionSource === 'full_image' ? 'full_image' : 'manual',
            categoryId: input.selectedCategoryId,
            confidence: 1
        }
    ];

    return {
        uploadImageId: input.request.upload_image_id,
        cropImageId,
        cropUrl: selectedCrop?.crop_url || null,
        localMockImageId: input.request.local_mock_image_id,
        imageUrl: input.request.image_url,
        imageSize: input.originalImageSize || selectedCrop?.original_image_size || input.request.original_image_size || null,
        selectedRegion: cloneData(input.selectedRegion),
        cropRegionOriginal,
        selectedCategoryId: input.selectedCategoryId,
        regionSource: input.regionSource,
        userConfirmed: input.userConfirmed ?? true,
        selectedRegionCandidates,
        categoryHints: [
            {
                categoryId: input.selectedCategoryId,
                source: 'user_selected',
                confidence: 1
            }
        ],
        categoryHint: {
            categoryId: input.selectedCategoryId,
            source: 'user_selected',
            confidence: 1
        },
        privacyFlags: input.privacyFlags || {
            allowExternalAnalysis: false,
            requiresOnDeviceProcessing: !input.request.upload_image_id,
            retainUploadedImage: Boolean(input.request.upload_image_id)
        }
    };
}

function sensitiveRegionDetectionRequestFromInput(
    input: CreateImageSearchRequestInput,
    localMockImageId: string | null
): SensitiveRegionDetectionRequest {
    return {
        uploadImageId: input.uploadImageId ?? null,
        localMockImageId,
        imageUrl: input.imageUrl,
        imageSize: input.imageSize ?? null,
        selectedRegion: input.croppedRegion ?? null,
        cropRegionOriginal: input.croppedRegion ?? null,
        privacyFlags: input.privacyFlags || {
            allowExternalAnalysis: false,
            requiresOnDeviceProcessing: true,
            retainUploadedImage: false
        }
    };
}

async function analyzeWithActiveImageAnalysisService(request: ImageAnalysisRequest): Promise<ImageAnalysisResponse> {
    try {
        return await activeImageAnalysisService.analyzeImage(request);
    } catch {
        return fallbackImageAnalysisResponse('이미지 분석 API 연결에 실패해 수동 영역 선택으로 전환합니다.');
    }
}

async function detectWithActiveSensitiveRegionService(
    request: SensitiveRegionDetectionRequest
): Promise<SensitiveRegionDetectionResponse> {
    try {
        return await activeSensitiveRegionService.detectSensitiveRegions(request);
    } catch {
        return {
            status: 'failed',
            candidates: [],
            needsUserReview: true,
            warning: '민감 영역 감지 서비스 연결에 실패했습니다.',
            policy: {
                originalExternalTransferAllowed: false,
                clothingCropSeparatedFromSensitiveRegions: true,
                detectionMayFail: true,
                provider: 'mock'
            }
        };
    }
}

export async function createImageSearchRequest(input: CreateImageSearchRequestInput): Promise<ImageSearchRequestSession> {
    const now = new Date();
    const nowIso = now.toISOString();
    const retentionDays = input.retentionDays ?? 30;
    const localMockImageId = input.localMockImageId ?? (input.uploadImageId ? null : `local-mock-image-${now.getTime()}`);
    const analysisResponse = input.detectedItems
        ? createMockImageAnalysisResponseFromDetectedItems(input.detectedItems)
        : await analyzeWithActiveImageAnalysisService(imageAnalysisRequestFromInput(input, localMockImageId));
    const sensitiveRegionDetection = input.privacyRedactions
        ? {
            status: 'detected' as const,
            candidates: cloneData(input.privacyRedactions),
            needsUserReview: input.privacyRedactions.length > 0,
            warning: null,
            policy: {
                originalExternalTransferAllowed: false as const,
                clothingCropSeparatedFromSensitiveRegions: true as const,
                detectionMayFail: true as const,
                provider: 'mock' as const
            }
        }
        : await detectWithActiveSensitiveRegionService(sensitiveRegionDetectionRequestFromInput(input, localMockImageId));
    const privacyRedactions = cloneData(sensitiveRegionDetection.candidates);
    const detectedItems = cloneData(analysisResponse.detectedItems) as DetectedSearchItem[];
    const requestId = `image-search-local-${now.getTime()}`;
    const detectedCategoryId = input.detectedCategoryId ?? null;
    const suggestedCategoryId = input.suggestedCategoryId ?? analysisResponse.suggestedCategoryId ?? null;
    const selectedCategoryId = input.selectedCategoryId ?? null;
    const searchTargets = input.searchTargets || createInitialSearchTargets(selectedCategoryId, nowIso);
    const activeSearchTargetId = input.activeSearchTargetId || (selectedCategoryId ? targetIdForCategory(selectedCategoryId) : null);
    const uiState = imageAnalysisUiState(analysisResponse);

    return {
        id: requestId,
        user_id: input.userId ?? null,
        upload_image_id: input.uploadImageId ?? null,
        local_mock_image_id: localMockImageId,
        image_url: input.imageUrl,
        cropped_region: input.croppedRegion ?? null,
        detected_items: detectedItems,
        image_analysis: analysisResponse,
        detected_category_id: detectedCategoryId,
        suggested_category_id: suggestedCategoryId,
        selected_item_index: null,
        selected_candidate_id: null,
        selected_category_id: selectedCategoryId,
        search_targets: searchTargets,
        active_search_target_id: activeSearchTargetId,
        search_status: searchStatusForImageAnalysis(analysisResponse),
        privacy_redactions: privacyRedactions,
        sensitive_region_detection_status: sensitiveRegionDetection.status,
        sensitive_region_detection_warning: sensitiveRegionDetection.warning,
        ui_state: uiState,
        created_at: nowIso,
        expires_at: addDays(now, retentionDays).toISOString()
    };
}

export async function selectDetectedItem(input: SelectDetectedItemInput): Promise<ImageSearchRequestSession> {
    const selectedItem = input.request.detected_items.find((item) => item.item_index === input.itemIndex) || null;
    const selectedTargetId = input.request.active_search_target_id;
    const searchTargets = (input.request.search_targets || []).map((target) => {
        if (target.id !== selectedTargetId) return target;

        return {
            ...target,
            selectedRegion: selectedItem?.cropped_region || target.selectedRegion,
            regionSource: selectedItem ? 'candidate' as SearchTargetRegionSource : target.regionSource,
            adjustmentStatus: selectedItem ? 'candidate' as SearchTargetAdjustmentStatus : target.adjustmentStatus,
            suggestedCategoryId: selectedItem?.suggestedCategoryId ?? target.suggestedCategoryId ?? null,
            suggestedCategoryConfidence: selectedItem ? categoryConfidenceForDetectedItem(selectedItem) : target.suggestedCategoryConfidence ?? null,
            selectedCandidateId: selectedItem?.id ?? null,
            selectedItemIndex: selectedItem?.item_index ?? null,
            updatedAt: new Date().toISOString()
        };
    });

    return {
        ...cloneData(input.request),
        cropped_region: selectedItem?.cropped_region || input.request.cropped_region,
        selected_item_index: selectedItem?.item_index ?? null,
        selected_candidate_id: selectedItem?.id ?? null,
        selected_category_id: input.request.selected_category_id,
        search_targets: searchTargets,
        search_status: 'matching'
    };
}

export async function analyzeConfirmedCrop(input: AnalyzeConfirmedCropInput): Promise<ImageAnalysisResponse> {
    return analyzeWithActiveImageAnalysisService(imageAnalysisRequestFromConfirmedCrop(input));
}

function cropImageIdFromSearchInput(input: SelectedRegionSearchRequest): string | null {
    return input.crop_image_id || input.selected_crop?.crop_image_id || input.local_crop_id || null;
}

function vectorIdFromSearchInput(input: SelectedRegionSearchRequest): string | null {
    const candidate = (input.image_embedding_candidates || []).find((item) => Boolean(item.vector_id?.trim())) || null;
    return candidate?.vector_id?.trim() || null;
}

function vectorSearchHintsFromTextCandidates(input: SelectedRegionSearchRequest): VectorSearchCategoryFilter {
    const embeddingRequest = input.embedding_search_request || null;
    const ocrHints = (embeddingRequest?.ocrHints || []).concat((input.ocr_candidates || []).map((candidate) => ({
        id: candidate.id,
        source: candidate.source,
        text: candidate.text,
        normalizedText: candidate.text.trim().toLowerCase(),
        confidence: candidate.confidence,
        boundingBox: candidate.boundingBox || null
    })));
    const logoHints = (input.logo_candidates || []).map((candidate) => ({
        id: candidate.id,
        source: 'logo' as const,
        text: candidate.text,
        normalizedText: candidate.text.trim().toLowerCase(),
        confidence: candidate.confidence,
        boundingBox: candidate.boundingBox || null
    }));

    return {
        selectedCategoryId: input.selected_category_id,
        brandHints: (embeddingRequest?.brandHints || []).concat(logoHints),
        ocrHints,
        wholesaleTagHints: []
    };
}

function vectorSearchCategoryFilterForInput(
    input: SelectedRegionSearchRequest,
    context: SearchCategoryContext
): VectorSearchCategoryFilter {
    const hints = vectorSearchHintsFromTextCandidates(input);
    return {
        ...hints,
        selectedCategoryId: input.selected_category_id,
        categoryRoot: context.rootLabel,
        subcategory: context.matchingSubcategoryLabel,
        subcategoryConfirmed: context.isSubcategoryConfirmed,
        includeMismatchedCandidates: false
    };
}

async function imageEmbeddingSearchForInput(
    input: SelectedRegionSearchRequest,
    context: SearchCategoryContext
): Promise<ImageEmbeddingSearchResponse | null> {
    const vectorId = vectorIdFromSearchInput(input);
    if (!vectorId) return null;

    try {
        const vectorResponse = await activeVectorSearchRepository.searchSimilarProducts(
            vectorId,
            vectorSearchCategoryFilterForInput(input, context),
            input.limit
        );

        return vectorSearchResponseToImageEmbeddingSearchResponse(vectorResponse, {
            sourceRequestId: input.embedding_search_request?.sourceRequestId || input.request_id,
            selectedCategoryId: input.selected_category_id
        });
    } catch {
        return null;
    }
}

function confidenceFromCandidatesAndClusters(
    productClusters: ProductCluster[],
    candidates: ImageEmbeddingSearchCandidate[]
): number {
    const candidateConfidence = candidates.reduce((best, candidate) => Math.max(best, candidate.finalScore), 0);
    const clusterConfidence = productClusters.reduce((best, cluster) => Math.max(best, cluster.confidence_level), 0);
    return Math.round(Math.max(candidateConfidence, clusterConfidence) * 1000) / 1000;
}

export function productSearchDisplayStatus(
    sourceStatus: SourceStatus,
    confidence: number
): ProductClusterSearchDisplayStatus {
    const normalizedConfidence = Math.max(0, Math.min(1, Math.round(confidence * 1000) / 1000));
    const confidenceLabel = normalizedConfidence >= 0.84
        ? '가능성 높음'
        : normalizedConfidence >= 0.7
            ? '추정 후보'
            : '참고 후보';

    if (sourceStatus === 'needs_verification') {
        return {
            source_status: sourceStatus,
            confidence: normalizedConfidence,
            label: '판매처 확인 후보',
            message: `${confidenceLabel}이지만 판매처와 매칭 근거 확인이 필요합니다.`
        };
    }

    if (sourceStatus === 'api_pending') {
        return {
            source_status: sourceStatus,
            confidence: normalizedConfidence,
            label: '판매처 확인 대기',
            message: '판매처 확인 대기 상태입니다. 확인 전 가격과 재고를 확정하지 않습니다.'
        };
    }

    if (sourceStatus === 'image_match_ready') {
        return {
            source_status: sourceStatus,
            confidence: normalizedConfidence,
            label: '이미지 매칭 후보',
            message: '선택 영역과 카테고리 신호로 찾은 후보입니다. 동일 상품 확정 표현은 사용하지 않습니다.'
        };
    }

    return {
        source_status: sourceStatus,
        confidence: normalizedConfidence,
        label: '검증용 후보',
        message: `${confidenceLabel}이며 실제 연동 전 검증용 카탈로그 기준입니다.`
    };
}

function displayStatusForGroup(
    productClusters: ProductCluster[],
    candidates: ImageEmbeddingSearchCandidate[]
): ProductClusterSearchDisplayStatus {
    return productSearchDisplayStatus(
        sourceStatusForEmbeddingGroup(productClusters, candidates),
        confidenceFromCandidatesAndClusters(productClusters, candidates)
    );
}

async function productClustersForEmbeddingCandidates(
    candidates: ImageEmbeddingSearchCandidate[],
    context: SearchCategoryContext
): Promise<ProductCluster[]> {
    const seen = new Set<string>();
    const clusters: ProductCluster[] = [];

    for (const candidate of candidates) {
        if (seen.has(candidate.candidateProductId)) continue;

        const cluster = await activeProductCatalogRepository.getProductClusterById(candidate.candidateProductId);
        if (!cluster) continue;

        const contextCluster = clusterWithEffectiveType(cluster, context);
        clusters.push({
            ...cloneData(contextCluster),
            cluster_type: candidate.section,
            confidence_level: Math.min(contextCluster.confidence_level, Math.max(candidate.finalScore, 0.01)),
            source_status: candidate.sourceStatus
        });
        seen.add(candidate.candidateProductId);
    }

    return clusters;
}

function sourceStatusForEmbeddingGroup(
    productClusters: ProductCluster[],
    candidates: ImageEmbeddingSearchCandidate[]
): SourceStatus {
    if (candidates.length === 0) return groupSourceStatus(productClusters);
    const statuses = candidates.map((candidate) => candidate.sourceStatus).concat(
        productClusters.map((cluster) => cluster.source_status)
    );
    if (statuses.some((sourceStatus) => sourceStatus === 'needs_verification')) return 'needs_verification';
    if (statuses.some((sourceStatus) => sourceStatus === 'mock')) return 'mock';
    if (statuses.some((sourceStatus) => sourceStatus === 'api_pending')) return 'api_pending';
    return 'image_match_ready';
}

interface PlatformSearchLookup {
    listings: Listing[];
    externalListings: Listing[];
    needsVerificationProductClusterIds: Set<string>;
    externalProviderFailed: boolean;
}

function queryHint(text: string | null | undefined, source: 'brand' | 'wholesale_tag' | 'ocr' | 'logo' | 'user_hint' | 'metadata', confidence = 0.62) {
    const trimmed = typeof text === 'string' ? text.trim() : '';
    if (!trimmed) return null;
    return {
        source,
        text: trimmed,
        normalizedText: trimmed.toLowerCase(),
        confidence
    };
}

function hintsFromTextCandidates(candidates: Array<{ id?: string; source?: string; text: string; confidence?: number | null }> | undefined) {
    return (candidates || [])
        .map((candidate) => ({
            id: candidate.id,
            source: candidate.source === 'logo' ? 'logo' as const : candidate.source === 'user_hint' ? 'user_hint' as const : 'ocr' as const,
            text: candidate.text,
            normalizedText: candidate.text.trim().toLowerCase(),
            confidence: candidate.confidence ?? 0.5
        }))
        .filter((candidate) => candidate.text.trim());
}

function queryBuilderInputForSelectedRegionSearch(
    productClusters: ProductCluster[],
    input: SelectedRegionSearchRequest,
    context: SearchCategoryContext,
    hasVisualEvidence: boolean
): NaverShoppingQueryBuilderInput {
    const embeddingRequest = input.embedding_search_request || null;
    const logoHints = hintsFromTextCandidates(input.logo_candidates).map((hint) => ({
        ...hint,
        source: 'logo' as const
    }));

    return {
        selectedCategoryId: input.selected_category_id,
        subcategoryHint: context.matchingSubcategoryLabel,
        brandHints: (embeddingRequest?.brandHints || []).concat(logoHints),
        ocrHints: (embeddingRequest?.ocrHints || [])
            .concat(hintsFromTextCandidates(input.ocr_candidates))
            .concat(hintsFromTextCandidates(input.attribute_candidates)),
        userSearchText: input.user_query_text || null,
        userTextHints: hintsFromTextCandidates(input.user_text_candidates).map((hint) => ({
            ...hint,
            source: 'user_hint' as const
        })),
        productClusters,
        hasVisualEvidence
    };
}

function queryBuilderInputForProductClusterSearch(
    productClusters: ProductCluster[],
    input: ProductClusterSearchApiRequest,
    hasVisualEvidence: boolean
): NaverShoppingQueryBuilderInput {
    return {
        selectedCategoryId: input.selectedCategoryId,
        subcategoryHint: input.subcategoryHint,
        brandHints: input.brandHints || [],
        ocrHints: input.ocrHints || [],
        wholesaleTagHint: input.wholesaleTagHint || null,
        wholesaleTagHints: input.wholesaleTagHints || [],
        colorHints: input.colorHints || [],
        materialHints: input.materialHints || [],
        patternHints: input.patternHints || [],
        fitHints: input.fitHints || [],
        productNameHints: input.productNameHints || [],
        userSearchText: input.userSearchText || null,
        userTextHints: input.userTextHints || [],
        productClusters,
        hasVisualEvidence
    };
}

function providerQueryForProductClusters(input: NaverShoppingQueryBuilderInput): NaverShoppingQueryBuilderResult {
    return buildNaverShoppingQuery(input);
}

function listingMatchesSearchContext(listing: Listing, context: SearchCategoryContext): boolean {
    if (context.rootLabel && listing.category_root && listing.category_root !== context.rootLabel) return false;
    if (context.rootLabel !== '하의') return true;

    const targetSubcategory = normalizeSubcategory(context.matchingSubcategoryLabel);
    const allowedSubcategories = compatibleExactSubcategories(targetSubcategory);
    if (!allowedSubcategories || !listing.subcategory) return true;

    return allowedSubcategories.has(listing.subcategory);
}

function productClustersWithPlatformEvidence(
    productClusters: ProductCluster[],
    lookup: PlatformSearchLookup
): ProductCluster[] {
    return productClusters.map((cluster) => {
        if (!lookup.needsVerificationProductClusterIds.has(cluster.id)) return cluster;

        return {
            ...cluster,
            confidence_level: Math.min(cluster.confidence_level, 0.74),
            source_status: 'needs_verification'
        };
    });
}

async function searchExternalPlatformListings(
    request: PlatformSearchProductsRequest,
    context: SearchCategoryContext
): Promise<Pick<PlatformSearchLookup, 'externalListings' | 'needsVerificationProductClusterIds' | 'externalProviderFailed'>> {
    const externalListings: Listing[] = [];
    let externalProviderFailed = false;

    if (!request.query?.trim()) {
        return {
            externalListings,
            needsVerificationProductClusterIds: new Set<string>(),
            externalProviderFailed
        };
    }

    for (const registry of activeExternalPlatformSearchRegistries) {
        try {
            const result = await registry.searchProducts(request);
            result.listings
                .filter((listing) => listingMatchesSearchContext(listing, context))
                .forEach((listing) => externalListings.push(listing));
        } catch {
            externalProviderFailed = true;
        }
    }

    return {
        externalListings,
        needsVerificationProductClusterIds: new Set(
            externalListings
                .filter((listing) => listing.source_status === 'needs_verification' || listing.freshness.sourceStatus === 'needs_verification')
                .map((listing) => listing.product_cluster_id)
        ),
        externalProviderFailed
    };
}

async function searchPlatformListingsForProductClusters(input: {
    productClusters: ProductCluster[];
    selectedCategoryId: string | null;
    selectedRegion: CroppedRegion | null;
    cropImageId?: string | null;
    cropUrl?: string | null;
    regionSource?: SearchTargetRegionSource | null;
    userConfirmed?: boolean | null;
    context: SearchCategoryContext;
    limit?: number | null;
    queryBuilderInput?: NaverShoppingQueryBuilderInput;
}): Promise<PlatformSearchLookup> {
    const productClusterIds = input.productClusters.map((cluster) => cluster.id);
    const baseRequest: PlatformSearchProductsRequest = {
        productClusterIds,
        selectedCategoryId: input.selectedCategoryId,
        selectedRegion: input.selectedRegion,
        cropImageId: input.cropImageId || null,
        cropUrl: input.cropUrl || null,
        regionSource: input.regionSource || null,
        userConfirmed: input.userConfirmed ?? null,
        includeSoldOut: true
    };
    const mockResults = await mockPlatformAdapterRegistry.searchProducts(baseRequest);
    const providerQuery = providerQueryForProductClusters(input.queryBuilderInput || {
        selectedCategoryId: input.selectedCategoryId,
        subcategoryHint: input.context.matchingSubcategoryLabel,
        productClusters: input.productClusters,
        hasVisualEvidence: input.userConfirmed === true || Boolean(input.cropImageId || input.cropUrl)
    });
    const externalResult = providerQuery.shouldCallProvider
        ? await searchExternalPlatformListings({
            ...baseRequest,
            query: providerQuery.query,
            limit: typeof input.limit === 'number' ? Math.max(1, Math.min(10, Math.floor(input.limit))) : Math.min(10, Math.max(1, productClusterIds.length))
        }, input.context)
        : {
            externalListings: [],
            needsVerificationProductClusterIds: new Set<string>(),
            externalProviderFailed: false
        };

    return {
        listings: mockResults.listings.concat(externalResult.externalListings),
        externalListings: externalResult.externalListings,
        needsVerificationProductClusterIds: externalResult.needsVerificationProductClusterIds,
        externalProviderFailed: externalResult.externalProviderFailed
    };
}

function selectedCropFromSearchInput(input: SelectedRegionSearchRequest): SelectedRegionCrop | null {
    const cropImageId = cropImageIdFromSearchInput(input);
    if (input.selected_crop) {
        return {
            ...cloneData(input.selected_crop),
            crop_image_id: input.selected_crop.crop_image_id || cropImageId,
            crop_region_original: input.selected_crop.crop_region_original || input.crop_region_original || null,
            selected_region: input.selected_crop.selected_region || cloneData(input.selected_region),
            original_image_size: input.selected_crop.original_image_size || input.original_image_size || null,
            displayed_image_size: input.selected_crop.displayed_image_size || input.displayed_image_size || null,
            crop_preview_url: input.selected_crop.crop_preview_url || input.crop_preview_url || null,
            local_crop_id: input.selected_crop.local_crop_id || input.local_crop_id || null
        };
    }

    if (!cropImageId && !input.crop_region_original) return null;

    return {
        crop_image_id: cropImageId,
        crop_url: null,
        crop_preview_url: input.crop_preview_url || null,
        local_crop_id: input.local_crop_id || null,
        selected_region: cloneData(input.selected_region),
        original_image_size: input.original_image_size || null,
        displayed_image_size: input.displayed_image_size || null,
        crop_region_original: input.crop_region_original || null,
        width: input.crop_region_original?.width || null,
        height: input.crop_region_original?.height || null,
        expires_at: null,
        crop_basis: input.region_source === 'full_image' ? 'full_image' : 'user_adjusted_region',
        background_removed: false,
        source_mask_url: null
    };
}

function productSearchLimit(limit?: number | null): number {
    if (typeof limit !== 'number' || !Number.isFinite(limit)) return 20;
    return Math.max(1, Math.min(50, Math.floor(limit)));
}

function selectedRegionFromProductSearchInput(input: ProductClusterSearchApiRequest): CroppedRegion {
    const selectedRegion = input.selectedRegion || input.selectedCrop?.selected_region || defaultRegionForCategory(input.selectedCategoryId);
    return cloneData(selectedRegion || { x: 0, y: 0, width: 100, height: 100 });
}

function vectorIdFromProductSearchInput(input: ProductClusterSearchApiRequest): string | null {
    const vectorId = input.vectorId?.trim();
    if (vectorId) return vectorId;
    const embeddingId = input.embeddingId?.trim();
    return embeddingId || null;
}

function vectorSearchCategoryFilterForProductSearchInput(input: ProductClusterSearchApiRequest): VectorSearchCategoryFilter {
    const subcategory = normalizeSubcategory(input.subcategoryHint || subcategoryFromCategoryId(input.selectedCategoryId));
    return {
        selectedCategoryId: input.selectedCategoryId,
        categoryRoot: categoryRootForId(input.selectedCategoryId),
        subcategory,
        subcategoryConfirmed: Boolean(input.subcategoryHint || subcategoryFromCategoryId(input.selectedCategoryId)),
        brandHints: input.brandHints || [],
        ocrHints: input.ocrHints || [],
        wholesaleTagHints: [],
        includeMismatchedCandidates: false
    };
}

async function imageEmbeddingSearchForProductSearchInput(
    input: ProductClusterSearchApiRequest,
    sourceRequestId: string,
    limit: number
): Promise<ImageEmbeddingSearchResponse | null> {
    const vectorId = vectorIdFromProductSearchInput(input);
    if (!vectorId) return null;

    try {
        const vectorResponse = await activeVectorSearchRepository.searchSimilarProducts(
            vectorId,
            vectorSearchCategoryFilterForProductSearchInput(input),
            limit
        );

        return vectorSearchResponseToImageEmbeddingSearchResponse(vectorResponse, {
            sourceRequestId,
            selectedCategoryId: input.selectedCategoryId
        });
    } catch {
        return null;
    }
}

async function productClustersForTextHints(
    input: ProductClusterSearchApiRequest,
    limit: number
): Promise<ProductCluster[]> {
    const queryQuality = buildNaverShoppingQuery(queryBuilderInputForProductClusterSearch([], input, false));
    if (queryQuality.status === 'needs_user_refinement') return [];

    const brandHints = (input.brandHints || [])
        .concat(input.wholesaleTagHint ? [queryHint(
            typeof input.wholesaleTagHint === 'string' ? input.wholesaleTagHint : input.wholesaleTagHint.text,
            'wholesale_tag',
            typeof input.wholesaleTagHint === 'string' ? 0.68 : input.wholesaleTagHint.confidence ?? 0.68
        )].filter((hint): hint is NonNullable<typeof hint> => Boolean(hint)) : [])
        .concat(input.wholesaleTagHints || []);
    const ocrHints = (input.ocrHints || [])
        .concat(input.userSearchText ? [queryHint(input.userSearchText, 'user_hint', 0.92)].filter((hint): hint is NonNullable<typeof hint> => Boolean(hint)) : [])
        .concat(input.userTextHints || [])
        .concat(input.productNameHints || [])
        .concat(input.colorHints || [])
        .concat(input.materialHints || [])
        .concat(input.patternHints || [])
        .concat(input.fitHints || []);
    const clusters = await activeProductCatalogRepository.searchByTextHints({
        selectedCategoryId: input.selectedCategoryId,
        subcategoryHint: input.subcategoryHint,
        brandHints,
        ocrHints,
        limit
    });

    if (queryQuality.sectionPromotionGuard.brandExactAllowed && queryQuality.sectionPromotionGuard.wholesaleSameAllowed) {
        return clusters;
    }

    return clusters.map((cluster) => {
        if (cluster.cluster_type === 'similar_style') return cluster;
        return {
            ...cluster,
            cluster_type: 'similar_style',
            confidence_level: Math.min(cluster.confidence_level, 0.62),
            source_status: 'needs_verification'
        };
    });
}

async function clustersWithListings(productClusters: ProductCluster[]): Promise<ProductCluster[]> {
    const clusters: ProductCluster[] = [];

    for (const cluster of productClusters) {
        const listings = await activeProductCatalogRepository.getListingsForCluster(cluster.id);
        if (listings.length > 0) clusters.push(cluster);
    }

    return clusters;
}

function productClusterSearchGroups(
    productClusters: ProductCluster[],
    candidates: ImageEmbeddingSearchCandidate[],
    context: SearchCategoryContext
): ProductClusterSearchApiResultGroup[] {
    return clusterOrder.map((clusterType) => {
        const groupedClusters = productClusters.filter((cluster) => cluster.cluster_type === clusterType);
        const embeddingCandidates = candidates.filter((candidate) => candidate.section === clusterType);
        const sourceStatus = sourceStatusForEmbeddingGroup(groupedClusters, embeddingCandidates);

        return {
            cluster_type: clusterType,
            label: groupLabel(clusterType, context),
            source_status: sourceStatus,
            display_status: productSearchDisplayStatus(
                sourceStatus,
                confidenceFromCandidatesAndClusters(groupedClusters, embeddingCandidates)
            ),
            embedding_candidates: embeddingCandidates,
            product_clusters: groupedClusters
        };
    });
}

export async function searchProductClusters(input: ProductClusterSearchApiRequest): Promise<ProductClusterSearchApiResponse> {
    const limit = productSearchLimit(input.limit);
    const sourceRequestId = input.sourceRequestId || input.requestId || `product-search-${Date.now()}`;
    const cropImageId = input.cropImageId || input.selectedCrop?.crop_image_id || null;
    const resolvedVectorId = vectorIdFromProductSearchInput(input);
    const selectedRegion = selectedRegionFromProductSearchInput(input);
    const context = searchContextForInput({
        selected_category_id: input.selectedCategoryId,
        selected_item_label: input.subcategoryHint ? `${categoryRootForId(input.selectedCategoryId) || ''} > ${input.subcategoryHint}` : null
    });
    const imageEmbeddingSearch = await imageEmbeddingSearchForProductSearchInput(input, sourceRequestId, limit);
    const candidateProductClusters = imageEmbeddingSearch?.candidates.length
        ? await productClustersForEmbeddingCandidates(imageEmbeddingSearch.candidates, context)
        : await productClustersForTextHints(input, limit);
    let productClusters: ProductCluster[];
    try {
        const platformLookup = await searchPlatformListingsForProductClusters({
            productClusters: candidateProductClusters,
            selectedCategoryId: input.selectedCategoryId,
            selectedRegion,
            cropImageId,
            cropUrl: input.cropUrl || input.selectedCrop?.crop_url || null,
            regionSource: input.regionSource,
            userConfirmed: input.userConfirmed,
            context,
            limit,
            queryBuilderInput: queryBuilderInputForProductClusterSearch(
                candidateProductClusters,
                input,
                Boolean(imageEmbeddingSearch?.candidates.length || resolvedVectorId)
            )
        });
        const productClusterIdsWithListings = new Set(platformLookup.listings.map((listing) => listing.product_cluster_id));
        productClusters = productClustersWithPlatformEvidence(
            candidateProductClusters.filter((cluster) => productClusterIdsWithListings.has(cluster.id)),
            platformLookup
        );
    } catch {
        productClusters = await clustersWithListings(candidateProductClusters);
    }
    const candidates = imageEmbeddingSearch?.candidates || [];
    const groups = productClusterSearchGroups(productClusters, candidates, context);
    const sourceStatus = candidates.length > 0
        ? sourceStatusForEmbeddingGroup(productClusters, candidates)
        : groupSourceStatus(productClusters);
    const displayStatus = productSearchDisplayStatus(
        sourceStatus,
        confidenceFromCandidatesAndClusters(productClusters, candidates)
    );

    return {
        ok: true,
        endpoint: '/api/lookit/products/search',
        request_id: sourceRequestId,
        source_request_id: sourceRequestId,
        upload_image_id: input.uploadImageId ?? null,
        local_mock_image_id: input.localMockImageId ?? null,
        crop_image_id: cropImageId,
        embedding_id: input.embeddingId || null,
        vector_id: resolvedVectorId,
        selected_region: selectedRegion,
        selected_category_id: input.selectedCategoryId ?? null,
        subcategory_hint: input.subcategoryHint ?? null,
        region_source: input.regionSource,
        user_confirmed: input.userConfirmed,
        limit,
        source_status: sourceStatus,
        display_status: displayStatus,
        image_embedding_search: imageEmbeddingSearch,
        groups,
        product_clusters: groups.flatMap((group) => group.product_clusters),
        selected_crop: input.selectedCrop || null
    };
}

export async function getSearchResults(input: GetSearchResultsInput): Promise<SearchResultsResponse> {
    const searchContext = searchContextForInput(input);
    const imageEmbeddingSearch = await imageEmbeddingSearchForInput(input, searchContext);
    const candidateProductClusters = imageEmbeddingSearch?.candidates.length
        ? await productClustersForEmbeddingCandidates(imageEmbeddingSearch.candidates, searchContext)
        : orderedProductClusters(input.limit, searchContext);
    let adapterListings: Listing[] = [];
    let platformErrorMessage = '';
    const cropImageId = cropImageIdFromSearchInput(input);
    const selectedCrop = selectedCropFromSearchInput(input);
    let platformLookup: PlatformSearchLookup | null = null;

    try {
        platformLookup = await searchPlatformListingsForProductClusters({
            productClusters: candidateProductClusters,
            selectedCategoryId: input.selected_category_id,
            selectedRegion: input.selected_region,
            cropImageId,
            cropUrl: selectedCrop?.crop_url || null,
            regionSource: input.region_source,
            userConfirmed: input.user_confirmed ?? true,
            context: searchContext,
            limit: input.limit,
            queryBuilderInput: queryBuilderInputForSelectedRegionSearch(
                candidateProductClusters,
                input,
                searchContext,
                Boolean(imageEmbeddingSearch?.candidates.length || input.image_embedding_candidates?.length || cropImageId)
            )
        });
        adapterListings = platformLookup.listings;
    } catch {
        platformErrorMessage = '플랫폼 상품/가격 조회가 실패했습니다. 같은 상품처럼 보이는 임의 결과는 표시하지 않습니다.';
    }

    const productClusterIdsWithListings = new Set(adapterListings.map((listing) => listing.product_cluster_id));
    const productClusters = productClustersWithPlatformEvidence(
        candidateProductClusters.filter((cluster) => productClusterIdsWithListings.has(cluster.id)),
        platformLookup || {
            listings: adapterListings,
            externalListings: [],
            needsVerificationProductClusterIds: new Set<string>(),
            externalProviderFailed: false
        }
    );
    const selectedSearchTarget = resolveSearchTarget(input);
    const groups = platformErrorMessage
        ? emptyGroupsForSearchContext(searchContext)
        : clusterOrder.map((clusterType) => {
            const groupedClusters = productClusters.filter((cluster) => cluster.cluster_type === clusterType);
            const embeddingCandidates = imageEmbeddingSearch?.candidates.filter((candidate) => candidate.section === clusterType) || [];
            return {
                cluster_type: clusterType,
                label: groupLabel(clusterType, searchContext),
                source_status: sourceStatusForEmbeddingGroup(groupedClusters, embeddingCandidates),
                embedding_candidates: embeddingCandidates,
                product_clusters: groupedClusters
            };
        });
    const uiState = resultUiState({
        productClusters,
        groups,
        candidateProductClusters,
        platformErrorMessage
    });
    const sourceStatus = platformErrorMessage
        ? 'needs_verification'
        : imageEmbeddingSearch?.candidates.length
            ? sourceStatusForEmbeddingGroup(productClusters, imageEmbeddingSearch.candidates)
            : groupSourceStatus(productClusters);

    return {
        request_id: input.request_id,
        upload_image_id: input.upload_image_id,
        local_mock_image_id: input.local_mock_image_id,
        selected_item_index: input.selected_item_index ?? null,
        selected_category_id: input.selected_category_id ?? null,
        suggested_category_id: input.suggested_category_id ?? null,
        detected_category_id: input.detected_category_id ?? null,
        selected_candidate_id: input.selected_candidate_id,
        selected_item_label: input.selected_item_label ?? null,
        crop_image_id: cropImageId,
        original_image_size: input.original_image_size || null,
        displayed_image_size: input.displayed_image_size || null,
        crop_region_original: input.crop_region_original || null,
        crop_preview_url: input.crop_preview_url || null,
        local_crop_id: input.local_crop_id || null,
        selected_crop: selectedCrop,
        selected_search_target: selectedSearchTarget,
        selected_region: input.selected_region,
        cropped_region: input.selected_region,
        source_status: sourceStatus,
        image_embedding_search: imageEmbeddingSearch,
        ui_state: uiState,
        groups,
        product_clusters: productClusters
    };
}

export async function getRecentImageSearchRequest(): Promise<ImageSearchRequest | null> {
    return lookitImageSearchRequests[0] ? cloneData(lookitImageSearchRequests[0]) : null;
}

export const searchService = {
    createImageSearchRequest,
    getInitialSearchRecommendation,
    initialSearchRecommendationPolicy,
    setImageAnalysisService,
    resetImageAnalysisService,
    setSensitiveRegionService,
    resetSensitiveRegionService,
    setVectorSearchRepository,
    resetVectorSearchRepository,
    setProductCatalogRepository,
    resetProductCatalogRepository,
    setProductSearchPlatformRegistries,
    resetProductSearchPlatformRegistries,
    createNaverShoppingSearchRegistry,
    useRuntimeImageAnalysisService,
    productSearchDisplayStatus,
    selectDetectedItem,
    analyzeConfirmedCrop,
    searchProductClusters,
    getSearchResults,
    getRecentImageSearchRequest
};
